import {DescriptionRounded, LibraryAddRounded} from "@mui/icons-material";
import {Chip, Grid, IconButton, Tooltip} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {AgGridReact} from "ag-grid-react";
import '../../style/SalesMarketing.css';
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import dayjs from "dayjs";
import PromoDetailModal from "./modals/PromoDetailModal";
import {usePromoActions} from "../../service/usePromoActions";
import {useStatusValuesPromo} from "../../component/useStatusValuesPromo";
import AdvancedFilter from "../../component/AdvancedFilter";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import Stack from "@mui/material/Stack";
import {PROMO_VISIBILITY_PRIVATE, PROMO_VISIBILITY_PUBLIC} from "../../component/Constants";
import * as Yup from 'yup';
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import EmptyState from "../../../../util/component/EmptyState";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {useHasScope} from "../../../../config/globalConfig";

const PromoListPage = () => {
    const {t} = useTranslation(['salesmarketing']);
    const navigate = useNavigate();
    const {globalData} = useContext(GlobalContext);
    const {getPromoList} = usePromoActions();
    const {getStatusColor, getStatusLabel} = useStatusValuesPromo();
    const [openPromoDetail, setOpenPromoDetail] = useState(false);
    const [selectedPromo, setSelectedPromo] = useState<any>(null);
    const [filters, setFilters] = useState<any>({
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        productCode: "",
        promoCode: "",
        visibility: ""
    });
    const [searchInput, setSearchInput] = useState("");
    const gridRef = useRef<AgGridReact>(null);
    const hasScope = useHasScope;

    const visibility = [
        {
            key: PROMO_VISIBILITY_PRIVATE,
            value: getStatusLabel(PROMO_VISIBILITY_PRIVATE)
        },
        {
            key: PROMO_VISIBILITY_PUBLIC,
            value: getStatusLabel(PROMO_VISIBILITY_PUBLIC)
        }
    ]

    const validationObj = {
        productCode: Yup.string().nullable(),
        promoCode: Yup.string().nullable(),
        visibility: Yup.string().nullable()
    }

    const defaultFilters = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        productCode: "",
        promoCode: "",
        visibility: ""
    }

    const fields = {
        promoCode: (field: any) => (
            <TextField
                fullWidth
                label={t('promo.filtersModal.promoCodeField')}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        productCode: (field: any) => (
            <TextField
                fullWidth
                label={t('promo.filtersModal.productCodeField')}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        visibility: (field: any) => (
            <TextField
                fullWidth
                label={t('promo.filtersModal.typeField')}
                select
                value={field.value}
                onChange={field.onChange}
            >
                <MenuItem key={""} value={""}/>
                {visibility?.map((a) => (
                    <MenuItem key={a.key} value={a.key}>
                        {a.value}
                    </MenuItem>
                ))}
            </TextField>
        )
    }

    const toolbar = {
        renderActions: () =>
            hasScope('SCOPE_SM_CREATE_PROMO')?
            <Button fullWidth  size="small" onClick={() => {
                navigate('/salesmarketing/promocreate')
            }}>
                <LibraryAddRounded style={{paddingRight: "10px"}}/>
                {t('promo.buttons.newPromo')}
            </Button>:<></>
    }

    const columns: ColDef[] = [
        {
            field: "promoCode",
            headerName: t('promo.listColumns.promoCode'),
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            unSortIcon: true,
            cellRenderer: (params: any) => params.value ? params.value : "-",
            maxWidth: 180
        },
        {
            field: "productCode",
            headerName: t('promo.listColumns.productCode'),
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            unSortIcon: true,
            maxWidth: 180
        },
        {
            field: "typology",
            headerName: t('promo.listColumns.typology'),
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            unSortIcon: true,
            maxWidth: 180,
            cellRenderer: (params: any) => params.value ? getStatusLabel(params.value) : '-',
        },
        {
            field: "tenantId",
            headerName: t('legalEntityField'),
            tooltipField: "legalEntity",
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true
        },
        {
            field: "dealerId",
            headerName: t('dealerField'),
            tooltipField: "dealer",
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true
        },
        {
            field: "description",
            headerName: t('promo.listColumns.description'),
            tooltipField: "description",
            unSortIcon: true
        },
        {
            field: "bookingStartDate",
            headerName: t('promo.listColumns.bookingStartDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : params.data?.monthlyStartDate ? dayjs(params.data?.monthlyStartDate).format("L") : "-",
            unSortIcon: true
        },
        {
            field: "bookingEndDate",
            headerName: t('promo.listColumns.bookingEndDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : params.data?.monthlyEndDate ? dayjs(params.data?.monthlyEndDate).format("L") : "-",
            unSortIcon: true
        },
        {
            field: "pickupStartDate",
            headerName: t('promo.listColumns.pickupStartDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "-",
            unSortIcon: true
        },
        {
            field: "pickupEndDate",
            headerName: t('promo.listColumns.pickupEndDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "-",
            unSortIcon: true
        },
        {
            field: "visibility",
            headerName: t('promo.listColumns.visibility'),
            cellRenderer: (params: any) => params.value ? getStatusLabel(params.value) : "",
            unSortIcon: true
        },
        {
            field: "statusCode",
            headerName: t('promo.listColumns.status'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            maxWidth: 160,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabel(params.data.statusCode)}
                             sx={{backgroundColor: getStatusColor(params.data.statusCode)}}/>
            }
        },
        {
            field: "_actions",
            headerName: t('promo.listColumns.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            maxWidth: 160,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            //navigate(`/salesmarketing/promoupdate/${params.data.id}`, {state: {promo: params.data}})
                            window.open(`/salesmarketing/promoupdate/${params.data.id}`, "_blank")
                        }} auth={'SCOPE_SM_UPDATE_PROMO'}/>
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setOpenPromoDetail(true);
                            setSelectedPromo(params.data);
                        }} auth={'SCOPE_SM_VIEW_PROMO_DETAIL'}/>
                    </Stack>
                );
            }
        }
    ];

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                let data = await getPromoList(filterTemp);
                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements

                });
                if (data.totalElements === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        };
    };

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 130
        },
        columnDefs: columns,
        noRowsOverlayComponentParams: {title: t('promo.noRows')}
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    return (
        <>
            <SectionHeader title={t('promo.listHeader.title')} subtitle={t('promo.listHeader.subTitle')}/>
            <AdvancedFilter validationObj={validationObj} defaultValues={defaultFilters} fields={fields}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable noRowsOverlayComponent={noRowsOverlayComponent} gridOptions={gridOptions}
                                       onGridReady={(params) => {
                                           params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                                       }}
                                       gridRef={gridRef} toolbar={toolbar} cacheBlockSize={10} paginationPageSize={10}
                                       serverSideInfiniteScroll/>
                </Grid>
            </Grid>
            {selectedPromo && <PromoDetailModal key={selectedPromo.id} open={openPromoDetail}
                                                handleClose={() => setOpenPromoDetail(false)}
                                                promo={selectedPromo}/>}
        </>
    );
}

export default PromoListPage;